import React, { ChangeEvent, useState } from 'react';
import { Box, 
    Image, 
    Badge, 
    Icon, 
    HStack, 
    Flex, 
    Text, 
    FormControl,
    FormLabel,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Select
} from "@chakra-ui/react";
import productsList from '../../assets/json/products.json';
import image from '../../assets/images/willian-justen-de-vasconcellos-xzJ92bNCXHA-unsplash.jpg'
import { BsStarFill } from 'react-icons/bs';
import { getCurrencySymbol } from '../../utilities/others';
import { FileOptionType, TProduct } from '../../types/ui';
import CustomPageFilters from '../../components/ui/filter/PageFilters';
import CustomModal from '../../components/ui/modals';
import CustomFileUI from '../../components/ui/file/File';
import FormInput from '../../components/ui/forms/FormInputText';

export default function ProductImages() {
    const [step, setStep] = useState(1)
    const [products, setProducts] = useState(productsList as TProduct[]);
    const [showAddProductModal, setShowAddProductModal] = useState(false);
    const [sortValue, setSortValue] = useState("");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [values, setValues] = useState<TProduct>({
        imageUrl: "",
        imageAlt: "",
        meta: {
            beds: 0,
            baths: 0,
            isNew: false,
        },
        title: "",
        formattedPrice: "",
        discountPrice: "",
        currency: "USD",
        reviewCount: 0,
        rating: 0,
    });
    const [files, setFiles] = useState<File>();

    const documentType: FileOptionType = {
        key: 'file',
        label: 'File', 
        accept: 'image/*'
    };

    function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
        const name = e.target.name;
        const value = e.target.value;

        setValues((prevState) => ({
            ...prevState,
            [name]: value
        }))
    }

    function handleAddProduct(product: TProduct) {
        setProducts([...products, product])
    };

    function handleFileChange(file: any) {                
        if (file.length && file[0]?.name){            
            setFiles(file)
            setValues((prevState) => ({
                ...prevState,
               imageUrl: file[0]?.name
            }))
        }
    }

    function addProductFooterBtnText() {
        if (step === 1) {
            return 'Next'
        } else {
            return 'Add product'
        }
    }

    function addProductFooterBtnAction() {
        if (step === 1) {
            setStep(2)
        } else {
            handleAddProduct(values)
            setShowAddProductModal(false)
        }
    }

    function addProductSecondaryBtnLabel() {
        if (step === 1) {
            return 'Cancel'
        } else {
            return 'Back'
        }
    }

    function addProductSecondaryBtnAction() {
        if (step === 1) {
            setShowAddProductModal(false);
        } else {
            setStep(1)
        }
    }

    function isFooterPrimaryBtnDisabled() {
        if (step === 1) {
            if (!values.title || !values.formattedPrice) {
                return true
            } else {
                return false
            }
        } else {
            return !files || !values.imageUrl
        }
    }

    return (
        <>
        {/* Top right content */}
            <Box>
                <CustomPageFilters 
                    title='users list'
                    isSearchDisabled={false}
                    fields={null}
                    isSearch
                    searchPlaceholder='Search products'
                    isNewButton
                    isOpen={isFilterOpen}
                    onOpen={() => setIsFilterOpen(true)}
                    onClose={() => setIsFilterOpen(false)}
                    addNewObj={{
                        pageType: 'product',
                        onClick: () => setShowAddProductModal(true)
                    }}
                    sortObj={{
                        sortFn: setSortValue,
                        sortValue
                    }}
                    isSort
                />
            </Box>
            <HStack width={'full'} wrap='wrap' spacing={'6'} paddingLeft='8' paddingRight={'4'} paddingY='8'>
                {products.map(item => (
                    <Box className='product-image-item' maxW="22rem" borderWidth="1px" rounded="lg" overflow="hidden">
                        <Image src={image} alt={item.imageAlt} />
                        <Box p="6" paddingLeft={'4'}>
                            <Flex alignItems="baseline">
                                {item.meta?.isNew && (
                                    <Badge rounded="full" px="2" colorScheme="blue">
                                        New
                                    </Badge>
                                )}
                                {item.meta && (
                                    <Box
                                        color="gray.500"
                                        fontWeight="semibold"
                                        letterSpacing="wide"
                                        fontSize="xs"
                                        textTransform="uppercase"
                                        ml="2"
                                    >
                                        {item.meta?.beds} beds &bull; {item.meta?.baths} baths
                                    </Box>
                                )}
                            </Flex>

                            <Text
                                mt="1"
                                fontWeight="semibold"
                                as="h4"
                                lineHeight="tight"
                                isTruncated
                            >
                                {item.title}
                            </Text>

                            <Box>
                                <span className={`text--sm ${item.discountPrice ? 'line-through color--text-gray' : ''}`}>
                                    {getCurrencySymbol(item.currency)}{item.formattedPrice}
                                </span>
                                {item.discountPrice && (
                                    <span className='text--sm ml--5'>{getCurrencySymbol(item.currency)}{item.discountPrice}</span>
                                )}
                                <Box as="span" color="gray.600" fontSize="sm">
                                / wk
                                </Box>
                            </Box>

                            <Flex mt="2" alignItems="center">
                                {Array(5)
                                .fill("")
                                .map((_, i) => (
                                    <Icon as={BsStarFill}
                                        key={i}
                                        color={i < item.rating ? "blue.500" : "gray.300"}
                                    />
                                ))}
                                <Box as="span" ml="2" color="gray.600" fontSize="sm">
                                    {item.reviewCount} reviews
                                </Box>
                            </Flex>
                        </Box>
                    </Box>
                ))}
            </HStack>

            {/* Add new document modal */}
            <CustomModal
                isOpen={showAddProductModal}
                onCloseFn={() => addProductSecondaryBtnAction()}
                isFooter={true}
                size={'xl'}
                headerTitle='Add a new product'
                footerPrimaryBtnLabel={addProductFooterBtnText()}
                footerPrimaryBtnDisabled={isFooterPrimaryBtnDisabled()}
                footerPrimaryBtnFn={() => addProductFooterBtnAction()}
                footerSecondaryBtnLabel={addProductSecondaryBtnLabel()}
            >
                <Box>
                    <form action="">
                        {step === 1 ? (
                            <Box>
                                <FormInput
                                    isRequired
                                    label='Product Name'
                                    id='title'
                                    value={values.title}
                                    name='title'
                                    onChange={handleInputChange}
                                    formControlProps={{ marginBottom: '4' }}
                                />
                                <FormControl marginBottom='4'>
                                    <FormLabel fontSize={'sm'} htmlFor='currency'>Currency</FormLabel>
                                    <Select 
                                        name='currency' 
                                        fontSize={'sm'} 
                                        id='currency' 
                                        placeholder='Select a currency' 
                                        defaultValue={values.currency}
                                    >
                                        {["NGN", "USD", "YEN", "GBP", "EUR"].map(item => (
                                            <option>{item}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl marginBottom='4'>
                                    <FormLabel htmlFor='beds' fontSize={'sm'}>No of beds</FormLabel>
                                    <NumberInput name='meta.beds'  max={50} min={0} size={'sm'}>
                                        <NumberInputField id='beds' />
                                        <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                </FormControl>
                                <FormControl marginBottom='4'>
                                    <FormLabel htmlFor='baths' fontSize={'sm'}>No of baths</FormLabel>
                                    <NumberInput name='meta.baths'  max={50} min={0} size={'sm'}>
                                        <NumberInputField id='baths' />
                                        <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                </FormControl>
                                <FormInput
                                    label='Price'
                                    id='price'
                                    value={values.formattedPrice}
                                    name='title'
                                    isRequired
                                    formControlProps={{ marginBottom: '4' }}
                                    onChange={handleInputChange}
                                />
                                <FormControl marginBottom='4'>
                                    <FormLabel fontSize={'sm'} htmlFor='reviews'>Product ratings</FormLabel>
                                    <Select name='ratings'  fontSize={'sm'} id='reviews' placeholder='Select rating'>
                                        {["1", "2", "3", "4", "5"].map(item => (
                                            <option>{item}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        ): (
                            <Box>
                                <FormControl marginBottom='4'>
                                    <FormLabel fontSize={'sm'} htmlFor='reviews'>Upload picture</FormLabel>
                                    <CustomFileUI 
                                        documentType={documentType} 
                                        files={files!} 
                                        onChange={handleFileChange} 
                                    />
                                </FormControl>
                            </Box>
                        )}
                    </form>
                </Box>
            </CustomModal>
        </>
    )
}